/**
 * Survey List shown on the Landing page.
 */

import { useEffect, useState } from "react";
import { Box, Flex, Heading } from "@chakra-ui/react";
import { SurveyList } from "../../config/Consts";
import { SurveyStates } from "../../config/types/SurveyStates";
import CompletedSurveyCard from "../surveys/cards/CompletedCard";
import LockedCard from "../surveys/cards/LockedCard";
import UnlockedSurveyCard from "../surveys/cards/UnlockedCard";
import { useGetSurveysInfoQuery } from "queries";
import useAuth from "hooks/useAuth";
import { calculateUnlockedTime } from "utils/getSurvey";

const getSurveyState = (survey: any): string => {
  if (survey.isCompleted as boolean) return SurveyStates.COMPLETED;
  else if (!(survey.isUnlocked as boolean)) return SurveyStates.LOCKED;
  else return SurveyStates.UNLOCKED;
};

const formattingSurveys = (surveys: any[]): any => {
  if (surveys === undefined || surveys === null) return;

  const arr = surveys.map((el: any) => {
    const { survey } = el;
    survey.key = survey.key?.trim(); // followUp3 having space in the end

    const diff = calculateUnlockedTime(el);

    let keyName = survey.name.split(" ");
    keyName = keyName.slice(0, keyName.length - 1);
    const { completeSms, earnedSmsAmount, totalSms, totalEarnedAmount } = el;
    return {
      [keyName.join("-").toLowerCase() ?? "personality"]: {
        title: survey?.name,
        description: survey?.description,
        estimatedTime: survey?.duration,
        surveyState: getSurveyState(el),
        maxSurveyCompensation: survey?.reward,
        unlockAt: diff,
        earnedCompensation: totalEarnedAmount ?? 0,
        isUnlocked: el.isUnlocked,
        id: survey?._id,
        completeSms,
        earnedSmsAmount,
        totalSms,
        reward: survey?.reward,
      },
    };
  });

  return Object.assign(
    {},
    ...arr.map((surveyDetail: any) => ({
      [Object.keys(surveyDetail)[0]]: Object.values(surveyDetail)[0],
    }))
  );
};

const SurveyListUI: () => JSX.Element = () => {
  const { data: surveysInfoRaw } = useGetSurveysInfoQuery();
  // Normalize: handle both raw array and axios-wrapped response { data: [] }
  const surveysInfo: any[] | undefined = Array.isArray(surveysInfoRaw)
    ? surveysInfoRaw
    : Array.isArray(surveysInfoRaw?.data)
    ? surveysInfoRaw.data
    : undefined;
  const [surveys, setSurveys] = useState(SurveyList);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      let dynamicSurveys = {};
      if (
        Boolean(surveysInfo) &&
        Array.isArray(surveysInfo) &&
        surveysInfo?.length > 0
      ) {
        dynamicSurveys = formattingSurveys(surveysInfo);
      }
      if (surveysInfo?.length === 3 || surveysInfo?.length === 4) {
        setSurveys(dynamicSurveys);
      } else {
        const combinedSurveys = { ...SurveyList, ...dynamicSurveys };
        setSurveys(combinedSurveys);
      }
    } else {
      setSurveys(SurveyList);
    }
  }, [surveysInfo, isAuthenticated]);

  return (
    <Box w="100vw" maxWidth={{ "3xl": "80%" }} id="surveys">
      <Box mt={{ base: 16, md: 24 }} px={{ base: 4, md: 20, lg: 32 }} w="full">
        <Heading
          as="h1"
          fontWeight="700"
          fontSize={[35, 35, 40]}
          color="teal.900"
        >
          Surveys
        </Heading>
        <Flex flexDir="column" w="full">
          {Object.keys(surveys)?.map((surveyIdentifier) =>
            surveys[surveyIdentifier].surveyState === SurveyStates.LOCKED ? (
              <LockedCard
                key={surveyIdentifier}
                surveyIdentifier={surveyIdentifier}
                survey={surveys[surveyIdentifier]}
              />
            ) : surveys[surveyIdentifier].surveyState ===
              SurveyStates.COMPLETED ? (
              <CompletedSurveyCard
                key={surveyIdentifier}
                surveyIdentifier={surveyIdentifier}
                survey={surveys[surveyIdentifier]}
              />
            ) : (
              <UnlockedSurveyCard
                key={surveyIdentifier}
                survey={surveys[surveyIdentifier]}
                surveyIdentifier={surveyIdentifier}
              />
            )
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default SurveyListUI;
